import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, RefreshCw } from 'lucide-react';
import { cardTheme } from '../../styles/cardTheme';
import { wrongAnswerService } from '../../services/wrongAnswerService';
import type { LessonWrongAnswers, WrongAnswerLesson } from '../../types/wrongAnswer';
import quizLessonIcon from '../../assets/images/curriculum/quiz.png';
import { BASE_URL } from '../../apiConfig';

const questionImageSrc = (path?: string) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
  return `${base}${path.replace(/^\//, '')}`;
};

const PortalMyMistakes: React.FC = () => {
  const { lessonId } = useParams<{ lessonId?: string }>();
  const navigate = useNavigate();
  const selectedLessonId = lessonId ? Number(lessonId) : null;

  const [lessons, setLessons] = useState<WrongAnswerLesson[]>([]);
  const [detail, setDetail] = useState<LessonWrongAnswers | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadLessons = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const result = await wrongAnswerService.getMyWrongAnswers();
    if (result.success) {
      setLessons(result.data);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  const loadDetail = useCallback(async (id: number) => {
    setIsLoading(true);
    setLoadError(null);
    setDetail(null);
    const result = await wrongAnswerService.getLessonWrongAnswers(id);
    if (result.success) {
      setDetail(result.data);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (selectedLessonId && Number.isFinite(selectedLessonId)) {
      loadDetail(selectedLessonId);
      return;
    }
    loadLessons();
  }, [selectedLessonId, loadDetail, loadLessons]);

  const listLesson = lessons.find((lesson) => lesson.lessonId === selectedLessonId);
  const title = detail?.lessonName || listLesson?.lessonName || 'تفاصيل الأخطاء';

  return (
    <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6 sm:py-6" dir="rtl">
      <div className="mb-4 flex items-start justify-between gap-3 sm:mb-6">
        <div className="min-w-0">
          {selectedLessonId ? (
            <button
              type="button"
              onClick={() => navigate('/student-portal/my-mistakes')}
              className="mb-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-green-800"
            >
              <ChevronLeft className="h-4 w-4 rotate-180" />
              أخطائي
            </button>
          ) : null}
          <h1 className={`text-xl font-bold sm:text-2xl ${cardTheme.title}`}>
            {selectedLessonId ? title : 'أخطائي'}
          </h1>
          <p className={`mt-1 text-sm sm:text-base ${cardTheme.subtitle}`}>
            {selectedLessonId
              ? 'إجابتك والإجابة الصحيحة لكل سؤال أخطأت فيه'
              : 'الاختبارات التي فيها إجابات خاطئة'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => (selectedLessonId ? loadDetail(selectedLessonId) : loadLessons())}
          disabled={isLoading}
          className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl border border-green-200 px-3 text-sm font-semibold text-green-800 hover:bg-green-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">تحديث</span>
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`${cardTheme.shell} h-20 animate-pulse bg-green-50/60`} />
          ))}
        </div>
      ) : loadError ? (
        <div className={`${cardTheme.shell} p-6 text-center sm:p-8`}>
          <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
          <p className="text-sm text-red-700 sm:text-base">{loadError}</p>
          <button
            type="button"
            onClick={() => (selectedLessonId ? loadDetail(selectedLessonId) : loadLessons())}
            className={`mt-4 inline-flex min-h-11 items-center justify-center rounded-xl px-5 text-sm font-bold ${cardTheme.btnPrimary}`}
          >
            إعادة المحاولة
          </button>
        </div>
      ) : selectedLessonId ? (
        detail && detail.questions.length > 0 ? (
          <div className="space-y-3">
            {detail.questions.map((question, index) => (
              <article key={`${question.questionText}-${index}`} className={`${cardTheme.shell} p-3.5 sm:p-5`}>
                <p className="mb-3 text-sm font-bold leading-relaxed text-green-900 sm:text-base">
                  {index + 1}. {question.questionText}
                </p>
                {question.imagePath ? (
                  <img
                    src={questionImageSrc(question.imagePath)}
                    alt=""
                    className="mb-3 max-h-48 w-full rounded-xl object-contain"
                  />
                ) : null}
                {question.reason ? (
                  <p className="mb-3 text-xs leading-relaxed text-gray-600 sm:text-sm">{question.reason}</p>
                ) : null}
                <div className="space-y-2">
                  <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                    <span className="mb-0.5 block text-[11px] font-semibold text-red-600">إجابتك</span>
                    {question.studentAnswer}
                  </p>
                  <p className="rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 text-sm text-green-900">
                    <span className="mb-0.5 block text-[11px] font-semibold text-green-700">الإجابة الصحيحة</span>
                    {question.correctAnswer}
                  </p>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={`${cardTheme.shell} p-8 text-center`}>
            <p className="text-sm font-bold text-green-900 sm:text-base">لا توجد أخطاء في هذا الدرس</p>
          </div>
        )
      ) : lessons.length === 0 ? (
        <div className={`${cardTheme.shell} p-8 text-center sm:p-10`}>
          <img src={quizLessonIcon} alt="" className="mx-auto mb-3 h-12 w-12 object-contain opacity-80" />
          <h2 className="text-base font-bold text-green-900 sm:text-lg">لا توجد أخطاء مسجّلة</h2>
          <p className={`mt-1 text-sm ${cardTheme.subtitle}`}>لما تسلّم اختبار وفيه إجابات غلط، هتظهر هنا.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {lessons.map((lesson) => (
            <button
              key={lesson.lessonId}
              type="button"
              onClick={() => navigate(`/student-portal/my-mistakes/${lesson.lessonId}`)}
              className={`${cardTheme.shell} ${cardTheme.shellHover} flex min-h-14 w-full items-center gap-3 p-3 text-right sm:p-4`}
            >
              <img src={quizLessonIcon} alt="" className="h-9 w-9 shrink-0 object-contain" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-green-900 sm:text-base">
                  {lesson.lessonName}
                </span>
                {(lesson.courseName || lesson.unitName) && (
                  <span className="mt-0.5 block truncate text-[11px] text-gray-500 sm:text-xs">
                    {[lesson.courseName, lesson.unitName].filter(Boolean).join(' · ')}
                  </span>
                )}
              </span>
              {lesson.wrongCount > 0 && (
                <span className={`${cardTheme.badgeGold} shrink-0 px-2.5 py-1 text-[11px]`}>
                  {lesson.wrongCount} {lesson.wrongCount === 1 ? 'خطأ' : 'أخطاء'}
                </span>
              )}
              <ChevronLeft className="h-4 w-4 shrink-0 text-green-700" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PortalMyMistakes;
